const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: MuiAccordion } = require('@mui/material/Accordion');
const { default: AccordionDetails } = require('@mui/material/AccordionDetails');
const { default: AccordionSummary } = require('@mui/material/AccordionSummary');
const { default: ExpandLessIcon } = require('@mui/icons-material/ExpandLess');
const { default: List } = require('@mui/material/List');
const { default: ListItem } = require('@mui/material/ListItemButton');
const { default: ListItemText } = require('@mui/material/ListItemText');
const { transient$Props } = require('utils/styles');
const { analyticsTemplates } = require('utils/analytics');

const internals = {};

module.exports = function ConversationStartersList(props) {

    const { conversationStarters = [], onItemClick, personalized = false, centered = false, defaultExpanded = centered, disabled = false } = props;

    const {
        Accordion,
        StyledAccordionSummary,
        StyledListRoot,
        StyledList,
        StyledListItem
    } = internals;

    const [expanded, setExpanded] = React.useState(defaultExpanded);
    const [focusedIndex, setFocusedIndex] = React.useState(0);
    const visible = conversationStarters;
    const itemRefs = React.useRef([]);

    React.useEffect(() => {

        if (personalized) {
            setFocusedIndex(0);
        }
    }, [conversationStarters, personalized]);

    const handleChange = (event, isExpanded) => {

        setExpanded(isExpanded);
        if (isExpanded) {
            setFocusedIndex(0);
            setTimeout(() => {
                itemRefs.current[0]?.focus();
            }, 0);
        }
    };

    const handleKeyDown = (event) => {

        const last = visible.length - 1;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const next = focusedIndex >= last ? 0 : focusedIndex + 1;
            setFocusedIndex(next);
            itemRefs.current[next]?.focus();
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prev = focusedIndex <= 0 ? last : focusedIndex - 1;
            setFocusedIndex(prev);
            itemRefs.current[prev]?.focus();
        }
    };

    return (
        <Accordion expanded={expanded} onChange={handleChange}>
            <StyledAccordionSummary
                expandIcon={<ExpandLessIcon />}
                aria-controls='panel1bh-content'
                id='panel1bh-header'
            >
                Conversation Starters
            </StyledAccordionSummary>
            <AccordionDetails>
                <StyledListRoot>
                    <StyledList
                        dense={!centered}
                        $centered={centered}
                        onKeyDown={handleKeyDown}
                        role='listbox'
                        aria-label='Conversation starters'
                    >
                        {visible.map(({ id, starterText }, index) => {

                            const displayText = centered ? starterText : internals.withoutGreeting(starterText);
                            return (

                            <StyledListItem
                                disabled={disabled}
                                key={`starter-${id}`}
                                ref={(el) => { itemRefs.current[index] = el; }}
                                tabIndex={focusedIndex === index ? 0 : -1}
                                onFocus={() => setFocusedIndex(index)}
                                onClick={() => {

                                    if (personalized) {
                                        analyticsTemplates.buttons('select conversation starter', 'personalized conversation starter selected', 'personalized');
                                    }
                                    else {
                                        analyticsTemplates.buttons('select conversation starter', `conversation starter: add conversation starter from dropdown menu  ${starterText || 'empty'}`, id);
                                    }
                                    onItemClick(displayText);
                                    setExpanded(false);
                                }}
                            >
                                <ListItemText primary={displayText} />
                            </StyledListItem>
                            );
                        })}
                    </StyledList>
                </StyledListRoot>
            </AccordionDetails>
        </Accordion>
    );
};

module.exports.propTypes = {
    conversationStarters: T.array,
    onItemClick: T.func,
    personalized: T.bool,
    centered: T.bool
};

internals.withoutGreeting = (text) => String(text || '').replace(/^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*\s*)?hi(?:\s*[,!:.-])?\s*/iu, '$1');

internals.Accordion = Styled(MuiAccordion)`
    && {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    &.Mui-expanded {
        margin: 0;
    }
`;

internals.StyledAccordionSummary = Styled(AccordionSummary)`
    && {
        min-height: 35px;
        align-items: center;
        position: relative;
        z-index: 2;
        background: ${({ theme }) => theme.palette.background.paper};
    }
`;

internals.StyledList = Styled(List, transient$Props)`
    && {
        width: 100%;
        background-color: ${({ theme }) => theme.palette.background.paper};
        position: relative;
        overflow: auto;
        max-height: ${({ $centered }) => $centered ? 'none' : '150px'};
        padding-top: 0;
        position: relative;
        z-index: 1;
    }
`;

internals.StyledListItem = Styled(ListItem)`
    && {
        cursor: pointer;
    }
`;

internals.StyledListRoot = Styled.div`
    && {
        width: 100%;
        background-color: ${({ theme }) => theme.palette.background.paper};
    }
`;
